
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedRatingTable,
    IGroupedRatingTableRowProps,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IFearOfCancerRecurrenceFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */



const ratings: IGroupedRatingTableRowProps[] = [
    {
        propertyPath: 'iAmWorriedAboutRecurrence'
    },
    {
        propertyPath: 'iAmAfraidOfRecurrence'
    },
    {
        propertyPath: 'iBelieveItIsNormal'
    },
    {
        propertyPath: 'iThinkOfUnpleasantThoughts'
    },
    {
        propertyPath: 'iBelieveImCured'
    },
]

const FearOfCancerRecurrenceForm: FunctionComponent<IFearOfCancerRecurrenceFormProps> = () => {
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey={"forms.fearOfCancerRecurrence"}
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iAmWorriedAboutRecurrence"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iAmWorriedAboutRecurrence"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iAmAfraidOfRecurrence"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iAmAfraidOfRecurrence"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iBelieveItIsNormal"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iBelieveItIsNormal"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iThinkOfUnpleasantThoughts"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iThinkOfUnpleasantThoughts"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iBelieveImCured"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iBelieveImCured"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="cancerRecurrenceOpinion"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="cancerRecurrenceOpinion"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughts"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughts"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughtsPerDay"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughtsPerDay"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="cancerRecurrencePeriodOfThought"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="cancerRecurrencePeriodOfThought"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }

                {
                    !showSteps && (
                        <>
                            <div>    
                                <GroupedRatingTable 
                                    rows={ratings}
                                    TableProps={{
                                        sx: {
                                            tableLayout: 'fixed'
                                        }
                                    }}
                                    LabelTableHeadCellProps={{
                                        sx: {
                                            width: '100%'
                                        }
                                    }}
                                    ValueTableHeadCellProps={{
                                        sx: {
                                            width: 150
                                        }
                                    }}
                                />
                            </div>
                            <GroupedField
                                propertyPath="cancerRecurrenceOpinion"
                                component={RadioGroup}
                                row={!isMobile}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughts"
                                component={RadioGroup}
                                row={!isMobile}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                            <GroupedField
                                propertyPath="frequencyOfCancerRecurrenceThoughtsPerDay"
                                component={RadioGroup}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                                row={!isMobile}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                            <GroupedField
                                propertyPath="cancerRecurrencePeriodOfThought"
                                component={RadioGroup}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between'
                                }}
                                row={!isMobile}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                        </>
                    )
                }
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default FearOfCancerRecurrenceForm